import React, { createContext, useContext, useMemo, useState } from "react";
import { Lock, Sparkles, ArrowRight } from "lucide-react";
import { useAuth } from "../../lib/auth";
import UpgradeModal from "../../components/UpgradeModal";
import { useNavigate } from "react-router-dom";

const PaywallContext = createContext({
  locked: false,
  featureName: "",
  showUpgrade: () => {},
});

/** Hook used inside any gated page (LeadGenerator, CreativeMaker, Messages) to
 *  check if the current user is on the free tier and to trigger the shared
 *  upgrade modal when they hit a locked action. */
export const usePaywall = () => useContext(PaywallContext);

/** Full-width inline banner shown at the top of gated pages for free users. */
const FreePreviewBanner = ({ featureName, onUpgrade }) => (
  <div
    data-testid="free-preview-banner"
    className="relative overflow-hidden bg-gradient-to-r from-[#0B3FA3] via-[#1D6AFF] to-[#3B82F6] text-white rounded-2xl px-5 sm:px-7 py-5 mb-6 shadow-[0_18px_40px_-15px_rgba(29,106,255,0.45)]"
  >
    <div className="absolute -right-12 -top-12 w-48 h-48 bg-white/10 rounded-full blur-3xl pointer-events-none" />
    <div className="relative flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-sm border border-white/25 flex items-center justify-center shrink-0">
          <Lock className="w-5 h-5" />
        </div>
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold bg-white/15 border border-white/25 rounded-full px-2 py-0.5 mb-1.5">
            <Sparkles className="w-3 h-3" /> Free preview
          </div>
          <h2 className="font-display text-lg sm:text-xl font-bold leading-snug">
            You're previewing {featureName}.
          </h2>
          <p className="text-sm text-white/85 mt-0.5">
            Sample data shown below. Upgrade to Pro to unlock real campaigns, live leads, and SMS.
          </p>
        </div>
      </div>
      <button
        data-testid="free-preview-cta"
        onClick={onUpgrade}
        className="inline-flex items-center gap-2 bg-white text-[#0B3FA3] hover:bg-amber-50 hover:text-[#1D6AFF] font-semibold rounded-xl px-5 py-2.5 transition-all shadow-[0_8px_20px_-6px_rgba(15,23,42,0.35)]"
      >
        Upgrade to Pro <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  </div>
);

/** Small inline upgrade nudge used by individual locked elements (e.g. a row
 *  of locked creative slots or a disabled "Launch Campaign" button). */
export const InlineUpgradeNudge = ({ feature = "this", className = "", testId }) => {
  const { showUpgrade } = usePaywall();
  return (
    <button
      data-testid={testId || `inline-upgrade-${feature.toLowerCase().replace(/\s+/g, '-')}`}
      onClick={() => showUpgrade(feature)}
      className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#1D6AFF] hover:text-[#1552C6] transition-colors ${className}`}
    >
      <Lock className="w-3 h-3" /> Upgrade to unlock
    </button>
  );
};

/** Floating lock overlay you can place on top of any container to dim it
 *  and surface an upgrade CTA. Children remain visible underneath. */
export const LockedOverlay = ({ feature = "this feature", message, testId, dense = false }) => {
  const { showUpgrade } = usePaywall();
  return (
    <div
      data-testid={testId || "locked-overlay"}
      className="absolute inset-0 z-20 flex items-center justify-center bg-white/55 backdrop-blur-[2px] rounded-2xl"
    >
      <div className={`text-center max-w-xs ${dense ? "p-3" : "p-5"}`}>
        <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#1D6AFF]/10 border border-[#1D6AFF]/30 mb-3">
          <Lock className="w-5 h-5 text-[#1D6AFF]" />
        </div>
        <div className="font-semibold text-slate-900 text-sm mb-1">Upgrade to unlock</div>
        <div className="text-xs text-slate-500 mb-3 leading-relaxed">
          {message || `${feature} is part of the Pro & Elite plans.`}
        </div>
        <button
          onClick={() => showUpgrade(feature)}
          className="inline-flex items-center gap-1.5 bg-[#1D6AFF] hover:bg-[#1552C6] text-white text-xs font-semibold rounded-lg px-3.5 py-2 transition-all"
        >
          <Sparkles className="w-3 h-3" /> Upgrade to Pro
        </button>
      </div>
    </div>
  );
};

/** Wraps a gated feature page. Always renders children (so free users see the
 *  full page populated with sample data), supplies the paywall context, and
 *  manages a single shared UpgradeModal for the page. */
export default function GatedPage({ children, featureName }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const locked = !user || user.plan === "free";
  const [modalReason, setModalReason] = useState(null);

  const value = useMemo(
    () => ({
      locked,
      featureName,
      showUpgrade: (reason) => setModalReason(reason || featureName),
    }),
    [locked, featureName]
  );

  return (
    <PaywallContext.Provider value={value}>
      <div data-testid={`gated-${featureName.toLowerCase().replace(/\s+/g, '-')}`}>
        {locked && (
          <FreePreviewBanner
            featureName={featureName}
            onUpgrade={() => setModalReason(featureName)}
          />
        )}
        {children}
        <UpgradeModal
          open={!!modalReason}
          onClose={() => setModalReason(null)}
          feature={modalReason || featureName}
        />
        {locked && (
          <div className="mt-10 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900 mb-1">
                Ready for real {featureName.toLowerCase()}?
              </h3>
              <p className="text-sm text-slate-500">
                Get live leads, SMS, and the full AI suite from $199/mo. Cancel anytime.
              </p>
            </div>
            <button
              data-testid="gated-footer-upgrade"
              onClick={() => navigate("/dashboard/plans")}
              className="inline-flex items-center gap-2 bg-[#1D6AFF] hover:bg-[#1552C6] text-white font-semibold rounded-xl px-5 py-2.5 transition-all"
            >
              <Sparkles className="w-4 h-4" /> Upgrade to Pro
            </button>
          </div>
        )}
      </div>
    </PaywallContext.Provider>
  );
}
